import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import { AdeHubMenu } from "@/components/AdeHubMenu";

// Custom ADE-style navigation - NOT Instagram copy
// Tabs: Now (feed), Here (nearby+connections), Alerts (notifications), Me (profile)

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showHub, setShowHub] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then((r) => setIsSuspended(!!r.data.is_suspended)).catch(() => {});
  }, []);
  
  // Enable swipe-right-to-go-back gesture
  useSwipeBack();

  // Register native push (APNs) on iOS app
  useEffect(() => {
    import("@/utils/nativePush").then((m) => m.initializeNativePush()).catch(() => {});
  }, []);
  
  // Check for unread notifications and messages
  useEffect(() => {
    const checkNotifications = async () => {
      try {
        const [notifRes, msgRes] = await Promise.all([
          api.get('/notifications/unread-count'),
          api.get('/chat/unread/count')
        ]);
        setUnreadNotifications((notifRes.data.count || 0) + (msgRes.data.count || 0));
      } catch (e) {
        // Ignore errors
      }
    };
    checkNotifications();
    const interval = setInterval(checkNotifications, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const navItems = [
    { 
      path: '/app/home', 
      labelKey: 'navNow',
      icon: (active) => (
        <svg viewBox="0 0 24 24" className="w-6 h-6">
          <defs>
            <linearGradient id="now-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF8C00" />
              <stop offset="50%" stopColor="#FF00CC" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          {/* Clock/moment icon */}
          <circle 
            cx="12" 
            cy="12" 
            r="10" 
            fill={active ? "url(#now-grad)" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="2"
          />
          <path 
            d="M12 6v6l4 2" 
            stroke={active ? "white" : "#9CA3AF"}
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      )
    },
    { 
      path: '/app/here', 
      labelKey: 'navHere',
      icon: (active) => (
        <svg viewBox="0 0 24 24" className="w-6 h-6">
          <defs>
            <linearGradient id="here-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF8C00" />
              <stop offset="50%" stopColor="#FF00CC" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          {/* Location pin with people */}
          <path 
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
            fill={active ? "url(#here-grad)" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="2"
          />
          <circle 
            cx="12" 
            cy="9" 
            r="2.5" 
            fill={active ? "white" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="1.5"
          />
        </svg>
      )
    },
    { isAdeHub: true },
    { 
      path: '/app/alerts', 
      labelKey: 'navAlerts',
      badge: unreadNotifications,
      icon: (active) => (
        <svg viewBox="0 0 24 24" className="w-6 h-6">
          <defs>
            <linearGradient id="alert-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF8C00" />
              <stop offset="50%" stopColor="#FF00CC" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          {/* Notification bell with ADE style */}
          <path 
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
            fill={active ? "url(#alert-grad)" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="2"
          />
          <path 
            d="M13.73 21a2 2 0 0 1-3.46 0"
            stroke={active ? "url(#alert-grad)" : "#9CA3AF"}
            strokeWidth="2"
            fill="none"
          />
        </svg>
      )
    },
    { 
      path: '/app/profile', 
      labelKey: 'navMe',
      icon: (active) => (
        <svg viewBox="0 0 24 24" className="w-6 h-6">
          <defs>
            <linearGradient id="me-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF8C00" />
              <stop offset="50%" stopColor="#FF00CC" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          {/* User silhouette */}
          <circle 
            cx="12" 
            cy="8" 
            r="4"
            fill={active ? "url(#me-grad)" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="2"
          />
          <path 
            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
            fill={active ? "url(#me-grad)" : "none"}
            stroke={active ? "none" : "#9CA3AF"}
            strokeWidth="2"
          />
        </svg>
      )
    },
  ];
  
  const isActive = (path) => {
    if (path === '/app/here') {
      return location.pathname.startsWith('/app/here') || 
             location.pathname === '/app/nearby' ||
             location.pathname === '/app/connections' ||
             location.pathname === '/app/requests';
    }
    if (path === '/app/alerts') {
      return location.pathname === '/app/alerts' || 
             location.pathname === '/app/notifications' ||
             location.pathname === '/app/messages' ||
             location.pathname.startsWith('/app/chat');
    }
    return location.pathname === path;
  };
  
  return (
    <div className="min-h-screen bg-white" style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px))' }} data-testid="dashboard">
      {/* Page Content */}
      <Outlet />
      
      {/* Bottom Navigation - ADE Original Style.
          Mounted via Portal on document.body: no ancestor (transform/filter/overflow)
          can ever detach position:fixed from the viewport (iOS Safari bug fix). */}
      {createPortal(
      <nav 
        className="ade-bottom-navigation fixed bottom-0 left-0 right-0 z-50" 
        data-testid="bottom-nav"
      >
        {/* Glass morphism background */}
        <div className="relative">
          {/* Top gradient accent line */}
          <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-pink-400/50 to-transparent" />
          
          {/* Nav container — padding-bottom follows the iPhone home indicator (safe area) */}
          <div className="bg-white/90 backdrop-blur-xl border-t border-gray-100/50" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 4px)' }}>
            <div className="flex justify-around items-end pt-2 pb-2 max-w-lg mx-auto">
              {navItems.map((item, idx) => {
                if (item.isAdeHub) {
                  return (
                    <button
                      key="ade-hub"
                      onClick={() => setShowHub(true)}
                      className="relative flex flex-col items-center px-2 min-w-[56px] -mt-7 active:scale-95 transition-transform"
                      data-testid="nav-ade-hub"
                    >
                      <div className="w-[60px] h-[60px] rounded-full bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500 p-[2.5px] shadow-xl shadow-pink-500/30">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                          <img
                            src="/images/ade-logo-extracted.png"
                            alt="ADE"
                            className="w-full h-full object-cover scale-[1.2]"
                          />
                        </div>
                      </div>
                    </button>
                  );
                }
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`relative flex flex-col items-center gap-1 px-2 py-1 min-w-[56px] transition-all duration-300 ${
                      active ? 'transform -translate-y-1' : 'hover:opacity-80 active:scale-95'
                    }`}
                    data-testid={`nav-${item.labelKey.replace('nav','').toLowerCase()}`}
                  >
                    {/* Active indicator - floating pill */}
                    {active && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-1 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 shadow-lg shadow-pink-500/30" />
                    )}
                    
                    {/* Icon container */}
                    <div className="relative">
                      {item.icon(active)}
                      
                      {/* Badge for notifications */}
                      {item.badge > 0 && (
                        <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500 text-white text-[10px] font-bold rounded-full px-1 shadow-lg">
                          {item.badge > 99 ? '99+' : item.badge}
                        </div>
                      )}
                    </div>
                    
                    {/* Label */}
                    <span className={`text-[11px] font-medium transition-all ${
                      active 
                        ? 'bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent font-semibold' 
                        : 'text-gray-400'
                    }`}>
                      {t(item.labelKey)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </nav>,
      document.body
      )}
      
      {/* ADE Hub Menu - all app features */}
      <AdeHubMenu isOpen={showHub} onClose={() => setShowHub(false)} />
    </div>
  );
}
